import { useState } from 'react';

const uid = () => Math.random().toString(36).slice(2, 8);

const Toggle = ({ on, onToggle }) => (
  <button onClick={onToggle} style={{
    width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: on ? '#25D366' : '#ccc', position: 'relative', transition: '0.2s', flexShrink: 0
  }}>
    <span style={{
      position: 'absolute', top: 3, left: on ? 22 : 3, width: 18, height: 18,
      borderRadius: '50%', background: '#fff', transition: '0.2s', display: 'block'
    }} />
  </button>
);

const AGENT_MODES = [
  { value: 'faq', label: 'FAQ Bot', icon: '❓', desc: 'Answers questions from your knowledge base' },
  { value: 'lead', label: 'Lead Qualifier', icon: '🎯', desc: 'Qualifies leads and routes to sales team' },
  { value: 'support', label: 'Support Agent', icon: '🛠️', desc: 'Handles customer support queries end-to-end' },
  { value: 'full', label: 'Full-Service Agent', icon: '🤖', desc: 'Handles FAQs, lead gen, support, and orders' },
];

const HANDOFF_TRIGGERS = [
  { value: 'escalation', label: 'Customer asks for human agent' },
  { value: 'no_answer', label: 'AI cannot answer after 2 attempts' },
  { value: 'high_value', label: 'Customer is high-value lead' },
  { value: 'negative', label: 'Customer expresses frustration' },
  { value: 'payment', label: 'Payment or billing topics' },
];

const TONE_OPTIONS = ['Professional', 'Friendly', 'Formal', 'Casual', 'Empathetic'];

const SAMPLE_FAQS = [
  { id: uid(), q: 'What are your business hours?', a: 'We are available Monday to Friday, 9 AM to 6 PM IST.' },
  { id: uid(), q: 'How do I place an order?', a: 'You can place an order by replying "ORDER" or visiting our website.' },
  { id: uid(), q: 'What is your refund policy?', a: 'We offer a 7-day hassle-free return policy on all products.' },
];

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'setup', label: '⚙️ Setup' },
  { key: 'knowledge', label: '📚 Knowledge Base' },
  { key: 'handoff', label: '👤 Human Handoff' },
  { key: 'preview', label: '👁 Preview' },
];

// ─── Setup Tab ────────────────────────────────────────────────────────────────
function SetupTab({ config, onChange }) {
  const upd = (k, v) => onChange({ ...config, [k]: v });

  return (
    <div>
      {/* Agent Mode */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 10 }}>
          Agent Mode
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {AGENT_MODES.map(m => (
            <button key={m.value} onClick={() => upd('mode', m.value)} style={{
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
              border: `2px solid ${config.mode === m.value ? '#25D366' : '#e5e7eb'}`,
              background: config.mode === m.value ? '#e8fdf2' : '#fff',
            }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{m.icon}</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: config.mode === m.value ? '#128C7E' : '#374151' }}>
                {m.label}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Agent Name */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
          Agent Name
        </label>
        <input value={config.agentName} onChange={e => upd('agentName', e.target.value)}
          placeholder="e.g. Aria, Max, Support Bot" style={{
            width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0',
            borderRadius: 8, fontSize: 14, boxSizing: 'border-box'
          }} />
      </div>

      {/* Welcome Message */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
          Agent Intro Message
        </label>
        <textarea value={config.introMessage} onChange={e => upd('introMessage', e.target.value)}
          rows={3} placeholder="Hi! I'm {{agent_name}}, your AI assistant. How can I help you today?"
          style={{
            width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0',
            borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
          }} />
      </div>

      {/* Tone */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Conversation Tone
        </label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TONE_OPTIONS.map(t => (
            <button key={t} onClick={() => upd('tone', t)} style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
              border: `1.5px solid ${config.tone === t ? '#25D366' : '#e0e0e0'}`,
              background: config.tone === t ? '#e8fdf2' : '#fff',
              color: config.tone === t ? '#128C7E' : '#555',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {/* Language */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
          Primary Language
        </label>
        <select value={config.language} onChange={e => upd('language', e.target.value)}
          style={{ padding: '7px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13 }}>
          {['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Bengali'].map(l => (
            <option key={l}>{l}</option>
          ))}
        </select>
      </div>

      {/* Toggles */}
      {[
        { key: 'collectLeads', label: 'Auto-collect name & email from new customers', icon: '📋' },
        { key: 'orderTracking', label: 'Enable order tracking queries (requires API integration)', icon: '📦' },
        { key: 'catalogSearch', label: 'Allow customers to browse product catalog', icon: '🛍️' },
      ].map(item => (
        <div key={item.key} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 0', borderBottom: '1px solid #f3f4f6'
        }}>
          <div style={{ fontSize: 13 }}>
            {item.icon} <span>{item.label}</span>
          </div>
          <Toggle on={config[item.key]} onToggle={() => upd(item.key, !config[item.key])} />
        </div>
      ))}
    </div>
  );
}

// ─── Knowledge Base Tab ───────────────────────────────────────────────────────
function KnowledgeTab({ faqs, onChange }) {
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [editId, setEditId] = useState(null);

  const addFaq = () => {
    if (newQ.trim() && newA.trim()) {
      onChange([...faqs, { id: uid(), q: newQ.trim(), a: newA.trim() }]);
      setNewQ(''); setNewA('');
    }
  };

  const removeFaq = (id) => onChange(faqs.filter(f => f.id !== id));

  const updateFaq = (id, key, val) =>
    onChange(faqs.map(f => f.id === id ? { ...f, [key]: val } : f));

  return (
    <div>
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 16
      }}>
        📚 Add your FAQs, product info, policies, and any text the AI should know. The more you add, the smarter it gets.
      </div>

      {/* Existing FAQs */}
      {faqs.map(faq => (
        <div key={faq.id} style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
          padding: 14, marginBottom: 10
        }}>
          {editId === faq.id ? (
            <div>
              <input value={faq.q} onChange={e => updateFaq(faq.id, 'q', e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
              <textarea value={faq.a} onChange={e => updateFaq(faq.id, 'a', e.target.value)}
                rows={2} style={{ width: '100%', padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              <button onClick={() => setEditId(null)} style={{
                marginTop: 6, padding: '5px 12px', background: '#25D366', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer'
              }}>Done</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 3 }}>Q: {faq.q}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>A: {faq.a}</div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setEditId(faq.id)} style={{
                  padding: '4px 10px', border: '1px solid #e0e0e0', borderRadius: 6,
                  background: '#fff', color: '#374151', fontSize: 11, cursor: 'pointer'
                }}>✏️</button>
                <button onClick={() => removeFaq(faq.id)} style={{
                  padding: '4px 8px', border: '1px solid #fee2e2', borderRadius: 6,
                  background: '#fff', color: '#ef4444', fontSize: 11, cursor: 'pointer'
                }}>🗑</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add new FAQ */}
      <div style={{
        background: '#f9fafb', border: '1.5px dashed #e5e7eb', borderRadius: 10, padding: 14, marginTop: 8
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>+ Add FAQ</div>
        <input value={newQ} onChange={e => setNewQ(e.target.value)}
          placeholder="Question (e.g. What is your return policy?)" style={{
            width: '100%', padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 6,
            fontSize: 13, marginBottom: 6, boxSizing: 'border-box'
          }} />
        <textarea value={newA} onChange={e => setNewA(e.target.value)}
          placeholder="Answer the AI should give…" rows={2} style={{
            width: '100%', padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 6,
            fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 8
          }} />
        <button onClick={addFaq} style={{
          padding: '7px 16px', background: '#128C7E', color: '#fff', border: 'none',
          borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer'
        }}>Add to Knowledge Base</button>
      </div>
    </div>
  );
}

// ─── Handoff Tab ──────────────────────────────────────────────────────────────
function HandoffTab({ handoff, onChange }) {
  const upd = (k, v) => onChange({ ...handoff, [k]: v });
  const toggleTrigger = (val) => {
    const set = new Set(handoff.triggers);
    set.has(val) ? set.delete(val) : set.add(val);
    upd('triggers', [...set]);
  };

  return (
    <div>
      <div style={{
        background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
        padding: '10px 14px', fontSize: 13, color: '#9a3412', marginBottom: 16
      }}>
        👤 Define when the AI should hand off the conversation to a human agent. Smooth handoffs improve customer satisfaction.
      </div>

      {/* Enable handoff */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', marginBottom: 14
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Enable Human Handoff</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>AI will route to agents when needed</div>
        </div>
        <Toggle on={handoff.enabled} onToggle={() => upd('enabled', !handoff.enabled)} />
      </div>

      {handoff.enabled && (<>
        {/* Trigger conditions */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
            Trigger Handoff When:
          </label>
          {HANDOFF_TRIGGERS.map(t => (
            <label key={t.value} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              background: '#fff', border: `1.5px solid ${handoff.triggers.includes(t.value) ? '#25D366' : '#e5e7eb'}`,
              borderRadius: 8, marginBottom: 6, cursor: 'pointer',
              background: handoff.triggers.includes(t.value) ? '#e8fdf2' : '#fff',
            }}>
              <input type="checkbox" checked={handoff.triggers.includes(t.value)}
                onChange={() => toggleTrigger(t.value)} />
              <span style={{ fontSize: 13 }}>{t.label}</span>
            </label>
          ))}
        </div>

        {/* Handoff message */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Handoff Message to Customer
          </label>
          <textarea value={handoff.message} onChange={e => upd('message', e.target.value)}
            rows={2} style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0',
              borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
            }} />
        </div>

        {/* Assign to */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Assign Handoff To
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {['Any available agent', 'Specific team', 'Specific agent'].map(opt => (
              <button key={opt} onClick={() => upd('assignTo', opt)} style={{
                padding: '6px 12px', fontSize: 12, borderRadius: 20, cursor: 'pointer', fontWeight: 500,
                border: `1.5px solid ${handoff.assignTo === opt ? '#25D366' : '#e0e0e0'}`,
                background: handoff.assignTo === opt ? '#e8fdf2' : '#fff',
                color: handoff.assignTo === opt ? '#128C7E' : '#555',
              }}>{opt}</button>
            ))}
          </div>
        </div>

        {/* Outside hours */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 0', borderTop: '1px solid #f3f4f6'
        }}>
          <div style={{ fontSize: 13 }}>
            🌙 Outside business hours — still attempt handoff?
          </div>
          <Toggle on={handoff.afterHours} onToggle={() => upd('afterHours', !handoff.afterHours)} />
        </div>
      </>)}
    </div>
  );
}

// ─── Preview Tab ──────────────────────────────────────────────────────────────
function PreviewTab({ config, faqs }) {
  const [messages, setMessages] = useState([
    { id: uid(), from: 'agent', text: config.introMessage || `Hi! I'm ${config.agentName || 'AI Agent'}, your virtual assistant. How can I help?` }
  ]);
  const [input, setInput] = useState('');

  const send = () => {
    const text = input.trim();
    if (!text) return;
    const userMsg = { id: uid(), from: 'user', text };
    setMessages(m => [...m, userMsg]);
    setInput('');

    // Simple FAQ match or fallback
    setTimeout(() => {
      const match = faqs.find(f => text.toLowerCase().includes(f.q.toLowerCase().split(' ')[1] || ''));
      const reply = match
        ? match.a
        : `Thanks for reaching out! I understand you're asking about "${text.substring(0, 30)}...". Let me check that for you.`;
      setMessages(m => [...m, { id: uid(), from: 'agent', text: reply }]);
    }, 800);
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
        👁 Simulated preview — see how your AI agent will respond.
      </div>
      <div style={{
        background: '#e5ddd5', borderRadius: 12, padding: 12, height: 360,
        display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto'
      }}>
        {messages.map(msg => (
          <div key={msg.id} style={{
            display: 'flex', justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start'
          }}>
            <div style={{
              maxWidth: '75%', padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.4,
              background: msg.from === 'user' ? '#dcf8c6' : '#fff',
              color: '#111',
              borderBottomRightRadius: msg.from === 'user' ? 2 : 10,
              borderBottomLeftRadius: msg.from === 'agent' ? 2 : 10,
            }}>
              {msg.from === 'agent' && (
                <div style={{ fontSize: 10, color: '#128C7E', fontWeight: 700, marginBottom: 2 }}>
                  🤖 {config.agentName || 'AI Agent'}
                </div>
              )}
              {msg.text}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message to test…" style={{
            flex: 1, padding: '9px 12px', border: '1px solid #e0e0e0',
            borderRadius: 20, fontSize: 13
          }} />
        <button onClick={send} style={{
          padding: '9px 18px', background: '#25D366', color: '#fff', border: 'none',
          borderRadius: 20, fontWeight: 700, fontSize: 13, cursor: 'pointer'
        }}>Send</button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AIAgent() {
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('setup');
  const [config, setConfig] = useState({
    mode: 'faq', agentName: 'Aria', tone: 'Friendly', language: 'English',
    introMessage: "Hi! I'm Aria, your AI assistant 🤖 How can I help you today?",
    collectLeads: true, orderTracking: false, catalogSearch: false,
  });
  const [faqs, setFaqs] = useState(SAMPLE_FAQS);
  const [handoff, setHandoff] = useState({
    enabled: true, triggers: ['escalation', 'no_answer'],
    message: 'Let me connect you with one of our team members who can better assist you.',
    assignTo: 'Any available agent', afterHours: false,
  });

  return (
    <div>
      {/* Header card */}
      <div style={{
        background: 'linear-gradient(135deg, #128C7E 0%, #25D366 100%)',
        borderRadius: 12, padding: '16px 20px', marginBottom: 16, color: '#fff',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>🤖 WhatsApp AI Agent</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 3 }}>
            Automate up to 80% of queries · 24/7 intelligent responses · Human handoff when needed
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, opacity: 0.9 }}>{agentEnabled ? 'Active' : 'Inactive'}</span>
          <Toggle on={agentEnabled} onToggle={() => setAgentEnabled(v => !v)} />
        </div>
      </div>

      {!agentEnabled && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10,
          padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 16
        }}>
          ⚠️ AI Agent is currently <strong>disabled</strong>. Configure settings below then toggle it on.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500,
            color: activeTab === tab.key ? '#128C7E' : '#6b7280',
            borderBottom: `2px solid ${activeTab === tab.key ? '#25D366' : 'transparent'}`,
            marginBottom: -2,
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'setup' && <SetupTab config={config} onChange={setConfig} />}
      {activeTab === 'knowledge' && <KnowledgeTab faqs={faqs} onChange={setFaqs} />}
      {activeTab === 'handoff' && <HandoffTab handoff={handoff} onChange={setHandoff} />}
      {activeTab === 'preview' && <PreviewTab config={config} faqs={faqs} />}

      {/* Save button */}
      {activeTab !== 'preview' && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <button style={{
            background: '#25D366', color: '#fff', border: 'none',
            padding: '10px 28px', borderRadius: 8, fontWeight: 700,
            cursor: 'pointer', fontSize: 14
          }}>Save & Apply</button>
        </div>
      )}
    </div>
  );
}